// Group-related type definitions

// MemberStatus lives in common-layer (cross-layer membership state). Imported for
// local use in this file and re-exported for backward compatibility.
import { MemberStatus } from '@communities/common-layer/types/membership.types'

export { MemberStatus }

export enum GroupType {
  PUBLIC = 'Public',
  PRIVATE = 'Private',
}

export enum GroupMemberRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export enum MembershipType {
  FREE = 'FREE',
  PAID = 'PAID',
}

export enum SubscriptionType {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum SubscriptionInterval {
  WEEKLY = 'WEEK',
  MONTHLY = 'MONTH',
  YEARLY = 'YEAR',
}

export enum NewsletterFrequency {
  OFF = 'OFF',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
}

export interface BaseNewsletterConfig {
  frequency: NewsletterFrequency
  timezone?: string
  firstRunAtUtc?: string | Date
  nextRunAtUtc?: string | Date
  isLastDay?: boolean
}

export interface DerivedNewsletterConfig {
  localTime?: string
  weekDay?: number
  monthDay?: number
}

/** GET contract from backend */
export type NewsletterConfigFromGetAPI = BaseNewsletterConfig

/** PATCH contract to backend */
export type NewsletterConfigToPatchAPI = Pick<BaseNewsletterConfig, 'frequency' | 'timezone' | 'isLastDay'> &
  Partial<Pick<DerivedNewsletterConfig, 'localTime' | 'weekDay' | 'monthDay'>>

/** UI working shape (base + optionally derived fields) */
export type NewsletterConfigUI = BaseNewsletterConfig & Partial<DerivedNewsletterConfig>

export interface IGroupBrandingColorPalette {
  background?: string
  tertiary?: string
  surface?: string
  fontPrimary?: string
  border?: string
  error?: string
  fill?: string
  primary?: string
  sidebarBackground?: string
}

export interface IGroupBrandingColor {
  theme?: string
  light?: IGroupBrandingColorPalette
  dark?: IGroupBrandingColorPalette
  windowGradient?: boolean
}

export interface IGroupBranding {
  brandColor?: string
  logo?: string
  favicon?: string
  cover?: string
  mode?: 'light' | 'dark'
  color?: IGroupBrandingColor
}
export interface GroupReward {
  title: string
  description: string
}
export interface IGroupCustomCode {
  customJs?: string
  customCss?: string
  headerCode?: string
  footerCode?: string
  liveMode?: boolean
}

export interface IGroupSettings {
  groupType?: GroupType
  membershipQuestionsEnabled?: boolean
  showMandatoryInfoModal?: boolean
  groupSwitcherVisibility?: boolean
  allowsMembersInvite?: boolean
  formId?: string
  gamificationSettings?: {
    rewards?: GroupReward[]
    levelNames?: Map<number, string>
  }
  membership?: {
    membershipType?: MembershipType
    subscriptionType?: SubscriptionType
    subscriptionInterval?: SubscriptionInterval
    price: number
    isLivePayment?: boolean
    trialDays: number
    currency: string
    paymentPriceId?: string
    paymentProductId?: string
    locationName?: string
  }
  isDiscoverable?: boolean
  newsletter?: NewsletterFrequency
  newsletterConfig?: NewsletterConfigFromGetAPI
  /** API accepts/returns array of { name, isVisible }; legacy string[] is still supported when reading */
  tabsConfiguration?: TabConfigurationItem[] | string[]
  customCode?: IGroupCustomCode
}

export interface TabConfigurationItem {
  name: string
  isVisible: boolean
  sequence?: number
  customLabel?: string
}

export type UpdateGroupTabsConfigurationItem = Omit<TabConfigurationItem, 'sequence'> & {
  sequence: number
}

export type BuildTabsConfigurationPayloadInputItem = Pick<TabConfigurationItem, 'name' | 'isVisible' | 'customLabel'>

export interface IPromotionalLink {
  description: string
  url: string
  private?: boolean
}

/** GET /groups/pinned list item (wire contract). */
export interface IPinnedGroupApiItem {
  id: string
  slug: string
  name: string
  type: string
  membersCount: number
  logo?: string
  coverImage?: string
}

/** POST /groups/{groupId}/pin 201 response (wire contract). */
export interface IGroupPinRecord {
  locationId: string
  userReferenceId: string
  source: string
  entityId: string
  entityType: string
  createdAt: string
}

export interface IGroup {
  _id: string
  slug: string
  name: string
  status?: string
  description?: string
  about?: string
  locationId: string
  membersCount?: number
  postsCount?: number
  media?: any[]
  branding?: IGroupBranding
  marketplaceGroupSlug?: string
  promotionalLinks?: Array<IPromotionalLink>
  createdBy: string
  createdAt: string
  updatedAt: string
  settings?: IGroupSettings
  isGokollabCreated?: boolean
}

/** Embedded membership on GET .../groups list items. Canonical API key: `userGroupDetails`. */
export interface EmbeddedUserGroupDetail {
  _id?: string
  contactId?: string
  groupId?: string
  locationId?: string
  status?: string
  role?: string
}

export type GroupListItemWithEmbeddedMembership = IGroup & {
  userGroupDetails?: EmbeddedUserGroupDetail
  /** @deprecated Prefer userGroupDetails */
  userGroupDetail?: EmbeddedUserGroupDetail
  /** @deprecated Kebab-case legacy key from older API responses */
  'user-group-detail'?: EmbeddedUserGroupDetail
}

export enum AnswerType {
  TEXT = 'TEXT_BOX',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
}

export interface CustomFieldRef {
  id: string
  key: string
}

export interface IMembershipQuestion {
  question: string
  answerType: AnswerType
  answerOptions?: string[]
  customField?: CustomFieldRef
}

export interface IMembershipQuestions {
  _id: string
  locationId: string
  groupId: string
  questions: IMembershipQuestion[]
  formSubmissionCount: number
  createdBy: string
  createdAt: string
  updatedAt: string
  __v: number
}

export interface IMembershipCustomField {
  _id: string
  question: string
  answerType: AnswerType
  options: string[]
  fieldKey: string
  isCustomField: boolean
}

export interface IMembershipCustomFieldGroup {
  folderName: string
  fields: IMembershipCustomField[]
}

export interface IMembershipAnswer {
  question: string
  answer: string | string[] | boolean[]
}

export interface IGroupMember {
  _id?: string
  contactId: string
  groupId: string
  userId?: string
  role: GroupMemberRole
  status: MemberStatus
  membershipAnswers?: IMembershipAnswer[]
  fullName?: string
  email?: string
  avatar?: string
  joinedAt?: string
  orderId?: string
  title?: string
  bio?: string
}
